import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react"
import {
  TimelinePlayer,
  type TimelinePlayerCapabilities,
  type TimelinePlayerPlaybackApi,
} from "@/components/timeline-player/timeline-player"
import {
  buildSingleVideoDocument,
  type EngineSources,
  type TimelineDocument,
  type TimelineSource,
} from "@/lib/timeline"
import type { PipSources } from "@/lib/player-pip"
import { DEFAULT_VIDEO_OUTPUT_FPS } from "@/lib/video-output-fps"

const EMPTY_FRAME_URLS: readonly string[] = []

export type SingleClipPlayerProps = {
  sourceId: number
  /** URL видео: same-origin proxy или redirect на хранилище. */
  streamUrl: string
  durationMs?: number | null
  frameRate?: number | null
  aspectRatio?: number | null
  timelineFrameUrls?: readonly string[]
  /** Подрезка внутри источника. Без неё играет весь файл. */
  sourceInMs?: number | null
  sourceOutMs?: number | null
  active?: boolean
  autoPlay?: boolean
  defaultLoop?: boolean
  showTrackHeaders?: boolean
  /** false — без линейки и дорожки кадров: видео, таймкод и транспорт (узкие панели просмотра). */
  showTimeline?: boolean
  className?: string
  /** Слот поверх кадра (метки, кнопки). Клики — у потомков с pointer-events-auto. */
  shellOverlay?: ReactNode
  onPlaybackReady?: (api: TimelinePlayerPlaybackApi | null) => void
  /** Натуральный размер кадра этого ролика. */
  onMediaSize?: (size: { width: number; height: number }) => void
  /** Без пропа плеер только смотрит. resize включает подрезку краёв одного клипа. */
  capabilities?: TimelinePlayerCapabilities
  /** reason="metadata" — дотяжка по loadedmetadata, не правка пользователя. */
  onDocChange?: (doc: TimelineDocument, meta?: { reason: "edit" | "metadata" }) => void
  /** Превиз и аниматик. Без пропа кнопки картинки в картинке нет. */
  pipSources?: PipSources | null
}

/**
 * Просмотр одного видео: весь файл или окно sourceIn/sourceOut.
 * Подрезка краёв — только если передан capabilities.resize.
 * isDefaultDoc — если длительность ещё неизвестна, клип дотягивается по loadedmetadata.
 * Дефолты autoPlay и loop совпадают с просмотром библиотеки и превью версии видео.
 */
export function SingleClipPlayer({
  sourceId,
  streamUrl,
  durationMs = null,
  frameRate = null,
  aspectRatio = null,
  timelineFrameUrls,
  sourceInMs = null,
  sourceOutMs = null,
  active = true,
  autoPlay = true,
  defaultLoop = true,
  showTrackHeaders = true,
  showTimeline = true,
  className,
  shellOverlay,
  onPlaybackReady,
  onMediaSize,
  pipSources = null,
  capabilities,
  onDocChange,
}: SingleClipPlayerProps) {
  const fps = frameRate != null && frameRate > 0 ? frameRate : DEFAULT_VIDEO_OUTPUT_FPS
  const frames = timelineFrameUrls ?? EMPTY_FRAME_URLS

  const builtDoc = useMemo(
    () =>
      buildSingleVideoDocument({
        sourceId,
        durationMs,
        sourceInMs: sourceInMs ?? undefined,
        sourceOutMs: sourceOutMs ?? undefined,
      }),
    [sourceId, durationMs, sourceInMs, sourceOutMs],
  )
  // Дотяжка source_out по loadedmetadata. Сброс при новом источнике, иначе останется окно прошлого файла.
  const [metaDoc, setMetaDoc] = useState<TimelineDocument | null>(null)
  const [editDoc, setEditDoc] = useState<TimelineDocument | null>(null)
  useEffect(() => {
    setMetaDoc(null)
    setEditDoc(null)
  }, [sourceId, durationMs, sourceInMs, sourceOutMs])
  const doc = editDoc ?? metaDoc ?? builtDoc
  const handleDocChange = useCallback(
    (next: TimelineDocument, meta?: { reason: "edit" | "metadata" }) => {
      if (meta?.reason === "metadata") {
        setMetaDoc(next)
      } else {
        setEditDoc(next)
      }
      onDocChange?.(next, meta)
    },
    [onDocChange],
  )

  const sources = useMemo<EngineSources>(
    () => ({ versionUrls: { [sourceId]: streamUrl }, assetUrls: {} }),
    [sourceId, streamUrl],
  )

  const handleMediaSize = useCallback(
    (versionId: number, size: { width: number; height: number }) => {
      if (versionId !== sourceId) return
      onMediaSize?.(size)
    },
    [onMediaSize, sourceId],
  )

  const pipResolve = useMemo(() => {
    if (pipSources == null) return null
    return (timeMs: number) => ({ localMs: Math.max(0, timeMs), sources: pipSources })
  }, [pipSources])

  const timelineSources = useMemo<Record<string, TimelineSource>>(
    () => ({
      [String(sourceId)]: {
        version_id: sourceId,
        stream_url: streamUrl,
        duration_ms: durationMs,
        frame_rate: fps,
        aspect_ratio: aspectRatio,
        timeline_frame_urls: [...frames],
      },
    }),
    [sourceId, streamUrl, durationMs, fps, aspectRatio, frames],
  )

  return (
    <TimelinePlayer
      doc={doc}
      sources={sources}
      timelineSources={timelineSources}
      aspectRatio={aspectRatio}
      frameRate={fps}
      mediaDurationMs={durationMs}
      active={active}
      autoPlay={autoPlay}
      defaultLoop={defaultLoop}
      capabilities={capabilities}
      isDefaultDoc={editDoc == null}
      onDocChange={handleDocChange}
      showTrackHeaders={showTrackHeaders}
      showTimeline={showTimeline}
      className={className}
      shellOverlay={shellOverlay}
      pipResolve={pipResolve}
      onPlaybackReady={onPlaybackReady}
      onMediaSize={onMediaSize ? handleMediaSize : undefined}
    />
  )
}
